/**
 * Panel de ranking de inscripción de clubes a un evento.
 * Muestra la posición de cada club según cuándo completó el mínimo de 10 inscritos + 1 pago.
 */

import { useMemo, useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Button,
} from "@/components/ui";
import {
  Trophy,
  Medal,
  Users,
  CheckCircle,
  Clock,
  Building2,
  DollarSign,
  Info,
  Star,
  X,
  Download,
} from "lucide-react";
import {
  useEventRankings,
  useEventRegistrations,
  useEventPayments,
} from "@/hooks/useEvents";
import { useAuthStore } from "@/stores/authStore";
import { usePermissions } from "@/hooks/usePermissions";
import type { Event } from "@/types";
import { exportToXlsx } from "@/lib/utils/exportXlsx";

const MIN_REGISTRATIONS = 10;

// Confeti animado — partículas CSS puras
const CONFETTI_COLORS = [
  "#f59e0b",
  "#10b981",
  "#6366f1",
  "#ec4899",
  "#f97316",
  "#14b8a6",
  "#8b5cf6",
  "#ef4444",
  "#3b82f6",
  "#84cc16",
];

function ConfettiPiece({ index }: { index: number }) {
  const color = CONFETTI_COLORS[index % CONFETTI_COLORS.length];
  const left = (index * 37 + 5) % 95;
  const delay = (index * 0.17) % 2.5;
  const duration = 2.2 + (index % 5) * 0.4;
  const size = 8 + (index % 4) * 4;
  const isCircle = index % 3 === 0;

  return (
    <div
      className="absolute top-0 pointer-events-none animate-bounce"
      style={{
        left: `${left}%`,
        width: size,
        height: isCircle ? size : size * 0.6,
        backgroundColor: color,
        borderRadius: isCircle ? "50%" : "2px",
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
        animationIterationCount: "infinite",
        transform: `rotate(${index * 23}deg)`,
        opacity: 0.85,
      }}
    />
  );
}

function CelebrationModal({
  position,
  clubName,
  completedAt,
  onClose,
}: {
  position: number;
  clubName: string;
  completedAt?: Date;
  onClose: () => void;
}) {
  const isTop3 = position <= 3;

  const positionConfig = {
    1: {
      emoji: "🏆",
      gradient: "from-amber-400 via-yellow-400 to-orange-400",
      textColor: "text-amber-700",
      bg: "from-amber-50 to-yellow-50",
      label: "¡PRIMER LUGAR!",
      subtext: "¡Fueron los más rápidos en completar el proceso!",
    },
    2: {
      emoji: "🥈",
      gradient: "from-gray-300 via-slate-300 to-gray-400",
      textColor: "text-gray-600",
      bg: "from-gray-50 to-slate-50",
      label: "¡SEGUNDO LUGAR!",
      subtext: "¡Excelente ejecución del equipo!",
    },
    3: {
      emoji: "🥉",
      gradient: "from-amber-600 via-orange-500 to-amber-700",
      textColor: "text-orange-700",
      bg: "from-orange-50 to-amber-50",
      label: "¡TERCER LUGAR!",
      subtext: "¡Muy bien, lo lograron en el top 3!",
    },
  };

  const config = positionConfig[position as 1 | 2 | 3] ?? {
    emoji: "⭐",
    gradient: "from-indigo-400 to-purple-500",
    textColor: "text-indigo-700",
    bg: "from-indigo-50 to-purple-50",
    label: `¡PUESTO #${position}!`,
    subtext: "¡Han completado el proceso de inscripción!",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Confeti */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 28 }).map((_, i) => (
          <ConfettiPiece key={i} index={i} />
        ))}
      </div>

      {/* Card central */}
      <div
        className="relative z-10 w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden"
        style={{
          animation: "celebration-pop 0.5s cubic-bezier(0.34,1.56,0.64,1) both",
        }}
      >
        <style>{`
          @keyframes celebration-pop {
            from { opacity: 0; transform: scale(0.6) translateY(30px); }
            to   { opacity: 1; transform: scale(1) translateY(0); }
          }
          @keyframes trophy-spin {
            0%   { transform: rotate(-15deg) scale(0.8); }
            50%  { transform: rotate(10deg) scale(1.15); }
            100% { transform: rotate(0deg) scale(1); }
          }
          @keyframes shimmer {
            0%   { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
        `}</style>

        {/* Header con gradiente */}
        <div
          className={`bg-gradient-to-br ${config.gradient} p-6 text-center relative`}
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-7 h-7 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>

          {/* Trofeo / emoji animado */}
          <div
            className="text-6xl mb-2 select-none"
            style={{ animation: "trophy-spin 0.7s ease-out 0.3s both" }}
          >
            {config.emoji}
          </div>

          <h2 className="text-2xl font-black text-white drop-shadow-md tracking-wide">
            {config.label}
          </h2>
        </div>

        {/* Contenido */}
        <div className={`bg-gradient-to-b ${config.bg} p-6 text-center`}>
          <div className="flex items-center justify-center gap-2 mb-3">
            <Building2 className="w-5 h-5 text-gray-500" />
            <p className="text-lg font-bold text-gray-800">{clubName}</p>
          </div>

          <p className={`text-sm font-semibold ${config.textColor} mb-1`}>
            {config.subtext}
          </p>

          <p className="text-xs text-gray-500 mb-1">
            Completaron <strong>10+ inscritos</strong> y subieron el comprobante
            de pago.
          </p>

          {completedAt && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs bg-white/80 rounded-full px-3 py-1.5 text-gray-500 font-medium shadow-sm">
              <Clock className="w-3 h-3" />
              {completedAt.toLocaleString("es-CO", {
                weekday: "short",
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          )}

          {isTop3 && (
            <div className="mt-4 flex justify-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="w-4 h-4 text-amber-400 fill-amber-400"
                  style={{ animationDelay: `${i * 0.1}s` }}
                />
              ))}
            </div>
          )}

          <Button
            className="mt-5 w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg shadow-indigo-500/25"
            onClick={onClose}
          >
            ¡Genial! 🎉
          </Button>
        </div>
      </div>
    </div>
  );
}

// Helper para convertir timestamp a Date de forma segura
const toSafeDate = (timestamp: unknown): Date => {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  if (
    typeof timestamp === "object" &&
    "toDate" in timestamp &&
    typeof (timestamp as { toDate: () => Date }).toDate === "function"
  ) {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  if (typeof timestamp === "object" && "seconds" in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  return new Date();
};

interface EventRankingPanelProps {
  event: Event;
}

export default function EventRankingPanel({ event }: EventRankingPanelProps) {
  const { data: rankings, isLoading: loadingRankings } = useEventRankings(
    event.id,
  );
  const { data: registrations } = useEventRegistrations(event.id);
  const { data: payments } = useEventPayments(event.id);
  // Bug fix 2026-04-27: antes usábamos `useAccessibleClubs()[0]` como "mi club",
  // lo que para un admin de asociación / unión devolvía cualquier club de su
  // scope (típicamente el primero por orden alfabético) y lo marcaba como
  // "Tu club" incorrectamente. Ahora derivamos el club personal SOLO desde
  // las membresías reales del usuario (o el club del que es director).
  const profile = useAuthStore((s) => s.profile);
  const myClubId =
    profile?.directorOfClub?.id ?? profile?.memberships?.[0]?.clubId;
  const { isUnionAdmin, isAssociationAdmin } = usePermissions();
  const canExport = isUnionAdmin || isAssociationAdmin;

  // Estado del modal de celebración
  const [celebration, setCelebration] = useState<{
    position: number;
    clubName: string;
    completedAt?: Date;
  } | null>(null);

  // Calcular datos por club (incluyendo los que no tienen ranking aún)
  const clubData = useMemo(() => {
    if (!registrations) return [];

    // Contar pagos por club
    const paymentsByClub: Record<string, number> = {};
    if (payments) {
      payments.forEach((p) => {
        paymentsByClub[p.clubId] = (paymentsByClub[p.clubId] || 0) + 1;
      });
    }

    const byClub: Record<
      string,
      {
        clubId: string;
        clubName: string;
        total: number;
        payments: number;
        position?: number;
        completedAt?: Date;
      }
    > = {};

    registrations.forEach((r) => {
      if (r.status === "cancelled" || r.status === "rejected") return;

      if (!byClub[r.clubId]) {
        byClub[r.clubId] = {
          clubId: r.clubId,
          clubName: r.clubName,
          total: 0,
          payments: paymentsByClub[r.clubId] || 0,
        };
      }
      byClub[r.clubId].total++;
    });

    // Enriquecer con datos del ranking
    if (rankings) {
      rankings.forEach((rk) => {
        if (byClub[rk.clubId]) {
          byClub[rk.clubId].position = rk.position;
          byClub[rk.clubId].completedAt = rk.completedAt
            ? toSafeDate(rk.completedAt)
            : undefined;
        }
      });
    }

    // Ordenar: primero los que tienen posición (ASC), luego los demás por payments DESC
    return Object.values(byClub).sort((a, b) => {
      if (a.position && b.position) return a.position - b.position;
      if (a.position) return -1;
      if (b.position) return 1;
      return b.payments - a.payments;
    });
  }, [registrations, rankings, payments]);

  // Detectar cuando MI club logra una posición en el ranking por primera vez
  useEffect(() => {
    if (!myClubId || !clubData.length) return;
    const my = clubData.find((c) => c.clubId === myClubId);
    if (!my?.position) return;

    const storageKey = `ranking_celebrated_${event.id}_${myClubId}`;
    if (localStorage.getItem(storageKey)) return; // ya se mostró

    localStorage.setItem(storageKey, String(my.position));
    setCelebration({
      position: my.position,
      clubName: my.clubName,
      completedAt: my.completedAt,
    });
  }, [clubData, myClubId, event.id]);

  const getPositionIcon = (position: number) => {
    if (position === 1) return <Trophy className="w-6 h-6 text-amber-500" />;
    if (position === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (position === 3) return <Medal className="w-6 h-6 text-amber-700" />;
    return (
      <span className="w-6 h-6 flex items-center justify-center text-sm font-bold text-gray-500">
        {position}
      </span>
    );
  };

  const getPositionBg = (position?: number) => {
    if (!position) return "bg-white";
    if (position === 1)
      return "bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200";
    if (position === 2)
      return "bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200";
    if (position === 3)
      return "bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200";
    return "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200";
  };

  // Export Excel (admin only)
  const handleExportExcel = () => {
    if (!clubData.length) return;
    const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
    const safeName = event.name.replace(/[^\w\s-]/g, "").replace(/\s+/g, "_");
    exportToXlsx({
      filename: `ranking_${safeName}_${stamp}`,
      sheetName: "Ranking",
      rows: clubData,
      columns: [
        {
          header: "Posición",
          key: (c) => (c.position ? c.position : ""),
          width: 10,
        },
        { header: "Club", key: (c) => c.clubName, width: 30 },
        { header: "Inscritos", key: (c) => c.total, width: 12 },
        { header: "Pagos", key: (c) => c.payments, width: 12 },
        {
          header: "Estado",
          key: (c) =>
            c.total >= MIN_REGISTRATIONS && c.payments >= 1
              ? "Completo"
              : "En progreso",
          width: 14,
        },
        {
          header: "Fecha completado",
          key: (c) =>
            c.completedAt
              ? c.completedAt.toLocaleDateString("es-CO", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "",
          width: 16,
        },
        {
          header: "Hora completado",
          key: (c) =>
            c.completedAt
              ? c.completedAt.toLocaleTimeString("es-CO", {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })
              : "",
          width: 14,
        },
      ],
    });
  };

  if (loadingRankings) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="py-12 text-center">
          <Clock className="w-8 h-8 text-gray-400 mx-auto animate-pulse" />
          <p className="text-gray-500 mt-2">Cargando ranking...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <CardTitle className="text-lg flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-md shadow-amber-500/25">
                  <Trophy className="w-5 h-5 text-white" />
                </div>
                Ranking de Inscripción
              </CardTitle>
              <p className="text-sm text-gray-500 ml-[52px]">
                Orden según completaron {MIN_REGISTRATIONS} inscritos + 1 pago
              </p>
            </div>
            {canExport && clubData.length > 0 && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleExportExcel}
                title="Descargar ranking en formato Excel/CSV"
                className="shrink-0"
              >
                <Download className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">Exportar Excel</span>
                <span className="sm:hidden">Excel</span>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {clubData.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">Aún no hay inscripciones</p>
            </div>
          ) : (
            <div className="space-y-3">
              {clubData.map((club) => {
                const progress = Math.min(
                  (club.total / MIN_REGISTRATIONS) * 100,
                  100,
                );
                const isComplete =
                  club.total >= MIN_REGISTRATIONS && club.payments >= 1;

                return (
                  <div
                    key={club.clubId}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-300 ${getPositionBg(club.position)} ${club.clubId === myClubId ? "ring-2 ring-purple-400 ring-offset-1" : ""}`}
                  >
                    {/* Posición */}
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center">
                      {club.position ? (
                        getPositionIcon(club.position)
                      ) : (
                        <Clock className="w-5 h-5 text-gray-400" />
                      )}
                    </div>

                    {/* Info del club */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <p className="font-semibold text-gray-900 truncate">
                          {club.clubName}
                        </p>
                        {club.clubId === myClubId && (
                          <Badge
                            className="bg-purple-100 text-purple-700"
                            size="xs"
                          >
                            Tu club
                          </Badge>
                        )}
                        {isComplete && (
                          <Badge variant="success" size="xs">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Completo
                          </Badge>
                        )}
                      </div>

                      {/* Barra de progreso */}
                      <div className="mt-2 flex items-center gap-3">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              isComplete
                                ? "bg-gradient-to-r from-emerald-400 to-emerald-600"
                                : "bg-gradient-to-r from-amber-400 to-orange-500"
                            }`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-gray-500 whitespace-nowrap">
                          <DollarSign className="w-3 h-3 inline mr-1" />
                          {club.payments} pago{club.payments !== 1 ? "s" : ""}
                        </span>
                      </div>

                      {/* Hora de completado */}
                      {club.completedAt && (
                        <p className="text-xs text-gray-500 mt-1">
                          Completado:{" "}
                          {club.completedAt.toLocaleString("es-CO", {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                          })}
                        </p>
                      )}
                    </div>

                    {/* Contador */}
                    <div className="flex-shrink-0 text-right">
                      <p className="text-2xl font-bold text-gray-900">
                        {club.total}
                      </p>
                      <p className="text-xs text-gray-500">inscritos</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Leyenda explicativa */}
          <div className="mt-4 p-3 bg-gray-50 rounded-xl flex items-start gap-2 text-xs text-gray-500">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>
              El ranking se determina por el orden en que cada club completa{" "}
              <strong>10 inscritos activos</strong> y sube al menos{" "}
              <strong>1 pago</strong>. El primero en lograrlo obtiene la
              posición #1. Los clubes sin completar aún aparecen al final con su
              progreso actual.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Modal de celebración cuando el club logra su posición */}
      {celebration && (
        <CelebrationModal
          position={celebration.position}
          clubName={celebration.clubName}
          completedAt={celebration.completedAt}
          onClose={() => setCelebration(null)}
        />
      )}
    </>
  );
}
